/**
 * ParticipantsInterface - Interface de gestion des participants
 * Version refactorisée utilisant les composants et hooks extraits
 */
import React, { useState, useMemo, useCallback, useEffect, memo } from 'react';

// Composants extraits
import DayTabNavigation from './DayTabNavigation';
import DayTeamLeaderSelector from './DayTeamLeaderSelector';
import SearchAndFilters from './SearchAndFilters';
import CompactParticipantItem from './CompactParticipantItem';
import ConflictAlert from './ConflictAlert';

// Composants externes
import ConflictResolutionModal from '../ConflictResolutionModal';
import QuickInterimForm from '../QuickInterimForm';

// Hooks extraits
import { useParticipantAvailability, useParticipantFiltering } from './hooks';

// Utils
import { API_ENDPOINTS } from '../../utils/apiConfig';
import { usePermissions } from '../../hooks/usePermissions';

const ParticipantsInterface = memo(({
  formData,
  setFormData,
  users = [],
  teams = [],
  teamLeaders = [],
  startDate,
  endDate,
  sessionId = null,
  token,
  showSuccess = () => {},
  showError = () => {},
  showWarning = () => {},
  showInfo = () => {},
  // Props multi-jours
  sessionDays = [],
  setSessionDays = () => {},
  dayParticipants = {},
  setDayParticipants = () => {},
  dayTeamLeaders = {},
  setDayTeamLeaders = () => {},
  participantOnSite = {},
  setParticipantOnSite = () => {},
  activeDayTab = 0,
  setActiveDayTab = () => {},
  multiDayInfo = { days: [], totalDays: 0 },
  onUsersRefresh = null
}) => {
  // États UI
  const [viewMode, setViewMode] = useState('participants');
  const [conflicts, setConflicts] = useState([]);
  const [loadingConflicts, setLoadingConflicts] = useState(false);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [showQuickInterimForm, setShowQuickInterimForm] = useState(false);
  const [localUsers, setLocalUsers] = useState([]);
  const [deletingDay, setDeletingDay] = useState(null);

  // Permissions
  const { hasPermission } = usePermissions(null);
  const canCreateInterim = hasPermission('users.create_interim') || hasPermission('users.manage');

  // Données calculées
  const isMultiDay = multiDayInfo.days.length > 1;
  const currentDay = multiDayInfo.days[activeDayTab];

  // Fusionner users et localUsers (intérimaires créés localement)
  const allUsers = useMemo(() => {
    const userIds = new Set(users.map(u => u.id));
    const newLocalUsers = localUsers.filter(u => !userIds.has(u.id));
    return [...users, ...newLocalUsers];
  }, [users, localUsers]);

  // Hook: Disponibilités
  const { userAvailability } = useParticipantAvailability({
    users: allUsers,
    currentDate: currentDay?.date || startDate,
    token,
    sessionId
  });

  // Participants actuels du jour
  const currentParticipants = useMemo(() => {
    if (currentDay && dayParticipants[currentDay.date]) {
      return dayParticipants[currentDay.date];
    }
    return [];
  }, [currentDay, dayParticipants]);

  // Set normalisé pour comparaisons
  const currentParticipantIds = useMemo(() => {
    return new Set(currentParticipants.map(id => String(id)));
  }, [currentParticipants]);

  // Helper: vérifier si participant
  const isParticipant = useCallback((userId) => {
    return currentParticipantIds.has(String(userId));
  }, [currentParticipantIds]);

  // Hook: Filtrage et pagination
  const {
    search,
    teamFilter,
    page,
    paginatedUsers,
    setSearch,
    setTeamFilter,
    setPage
  } = useParticipantFiltering({
    users: allUsers,
    userAvailability,
    currentParticipantIds
  });

  // Participants sélectionnés avec placeholders pour manquants
  const selectedParticipants = useMemo(() => {
    const found = [];
    const foundIds = new Set();

    allUsers.forEach(user => {
      if (currentParticipantIds.has(String(user.id))) {
        found.push(user);
        foundIds.add(String(user.id));
      }
    });

    currentParticipants.forEach(participantId => {
      const idStr = String(participantId);
      if (!foundIds.has(idStr)) {
        found.push({
          id: participantId,
          full_name: `Utilisateur #${idStr.substring(0, 8)}...`,
          first_name: 'Utilisateur',
          last_name: 'inconnu',
          group_name: 'Non chargé',
          role_name: '-',
          _isMissing: true
        });
      }
    });

    return found;
  }, [allUsers, currentParticipantIds, currentParticipants]);

  // Détection des conflits
  useEffect(() => {
    if (!userAvailability || Object.keys(userAvailability).length === 0) return;

    const detectedConflicts = [];
    currentParticipants.forEach(userId => {
      const availability = userAvailability[userId];
      const user = allUsers.find(u => u.id === userId);
      if (!user) return;

      if (availability?.status === 'hr_unavailable') {
        detectedConflicts.push({
          user_id: userId,
          user_name: user.full_name,
          isUnsaved: false,
          conflicts: [{
            reason: 'leave',
            details: availability.reason || 'Indisponibilité RH',
            type: 'hr_unavailable'
          }]
        });
      }

      if (availability?.status === 'busy') {
        const conflictingSession = availability.conflicting_sessions?.[0];
        detectedConflicts.push({
          user_id: userId,
          user_name: user.full_name,
          isUnsaved: false,
          conflicts: [{
            reason: 'double_booking',
            details: conflictingSession
              ? `Déjà affecté sur ${conflictingSession.room_name || conflictingSession.room_code}`
              : 'Déjà affecté sur une autre prestation',
            type: 'double_booking'
          }]
        });
      }
    });

    setConflicts(prev => {
      const manualConflicts = prev.filter(c =>
        c.conflicts?.[0]?.type !== 'hr_unavailable' &&
        c.conflicts?.[0]?.type !== 'double_booking'
      );
      return [...manualConflicts, ...detectedConflicts];
    });
  }, [currentParticipants, userAvailability, allUsers]);

  // Handler: Supprimer un jour
  const handleDeleteDay = useCallback(async (day, index) => {
    if (deletingDay) return;

    if (multiDayInfo.days.length <= 1) {
      showWarning('Impossible de supprimer le dernier jour de la prestation');
      return;
    }

    if (!window.confirm(`Supprimer le ${day.label || day.date} ?\n\nLes participants et données associés seront également supprimés.`)) {
      return;
    }

    if (!sessionId) {
      // Suppression locale
      const newDays = multiDayInfo.days.filter((_, i) => i !== index);
      setSessionDays(newDays.map((d, i) => ({ ...d, dayNumber: i + 1 })));
      setDayParticipants(prev => {
        const updated = { ...prev };
        delete updated[day.date];
        return updated;
      });
      if (activeDayTab >= newDays.length) {
        setActiveDayTab(Math.max(0, newDays.length - 1));
      }
      showSuccess('Jour supprimé');
      return;
    }

    // Suppression via API
    try {
      setDeletingDay(day.date);
      const response = await fetch(API_ENDPOINTS.PRESTATION_DELETE_DAY(sessionId, day.id), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        const newDays = multiDayInfo.days.filter((_, i) => i !== index);
        setSessionDays(newDays.map((d, i) => ({ ...d, dayNumber: i + 1 })));
        setDayParticipants(prev => {
          const updated = { ...prev };
          delete updated[day.date];
          return updated;
        });
        if (activeDayTab >= newDays.length) {
          setActiveDayTab(Math.max(0, newDays.length - 1));
        }
        showSuccess(`Jour du ${day.date} supprimé`);
      } else {
        showError(data.error || 'Erreur lors de la suppression');
      }
    } catch (err) {
      console.error('Erreur suppression jour:', err);
      showError('Erreur de connexion');
    } finally {
      setDeletingDay(null);
    }
  }, [sessionId, token, multiDayInfo.days, activeDayTab, setSessionDays, setDayParticipants, setActiveDayTab, showSuccess, showError, showWarning, deletingDay]);

  // Handler: Création intérimaire (nouveau)
  const handleInterimCreated = useCallback((newUser) => {
    setLocalUsers(prev => [...prev, {
      id: newUser.id,
      email: newUser.email,
      first_name: newUser.first_name,
      last_name: newUser.last_name,
      full_name: `${newUser.first_name} ${newUser.last_name}`,
      role_name: 'Employés',
      employee_status: 'Interim',
      group_id: null,
      group_name: 'Sans groupe',
      pending_review: true
    }]);

    if (currentDay?.date) {
      setDayParticipants(prev => ({
        ...prev,
        [currentDay.date]: [...(prev[currentDay.date] || []), newUser.id]
      }));
    }

    showSuccess(`${newUser.first_name} ${newUser.last_name} créé et ajouté aux participants`);
    onUsersRefresh?.();
  }, [currentDay, setDayParticipants, showSuccess, onUsersRefresh]);

  // Handler: Sélection intérimaire existant
  const handleSelectExistingInterim = useCallback((existingUser) => {
    // Vérifier si pas déjà participant
    if (currentDay?.date && isParticipant(existingUser.id)) {
      showWarning(`${existingUser.full_name} est déjà participant`);
      setShowQuickInterimForm(false);
      return;
    }

    // Ajouter aux users locaux s'il n'y est pas déjà
    if (!allUsers.some(u => u.id === existingUser.id)) {
      setLocalUsers(prev => [...prev, {
        id: existingUser.id,
        email: existingUser.email || '',
        first_name: existingUser.first_name,
        last_name: existingUser.last_name,
        full_name: existingUser.full_name,
        role_name: 'Employés',
        employee_status: 'Interim',
        group_id: null,
        group_name: existingUser.agency_name || 'Sans groupe'
      }]);
    }

    // Ajouter comme participant
    if (currentDay?.date) {
      setDayParticipants(prev => ({
        ...prev,
        [currentDay.date]: [...(prev[currentDay.date] || []), existingUser.id]
      }));
    }

    showSuccess(`${existingUser.full_name} ajouté aux participants`);
    setShowQuickInterimForm(false);
  }, [currentDay, isParticipant, allUsers, setDayParticipants, showSuccess, showWarning]);

  // Handler: Toggle participant
  const toggleParticipant = useCallback(async (userId) => {
    if (isParticipant(userId)) {
      setDayParticipants(prev => ({
        ...prev,
        [currentDay.date]: prev[currentDay.date]?.filter(id => id !== userId) || []
      }));
      setConflicts(prev => prev.filter(c => c.user_id !== userId));
      setParticipantOnSite(prev => {
        const updated = { ...prev };
        if (updated[currentDay.date]?.[userId]) {
          delete updated[currentDay.date][userId];
        }
        return updated;
      });
    } else {
      // Vérifier les conflits avant d'ajouter
      try {
        const response = await fetch(API_ENDPOINTS.PRESTATION_CHECK_CONFLICTS, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            participant_ids: [userId],
            start_date: currentDay.date,
            end_date: currentDay.date,
            exclude_session_id: sessionId
          })
        });

        if (response.ok) {
          const data = await response.json();
          const userConflicts = data.data || [];
          const thisUserConflicts = userConflicts.filter(c => c.user_id === userId);

          if (thisUserConflicts.length > 0) {
            const user = allUsers.find(u => u.id === userId);
            const conflictInfo = thisUserConflicts[0];
            const prestationNumbers = conflictInfo.conflicts?.map(c => c.prestation_number).join(', ') || 'autre prestation';
            showWarning(
              `${user?.full_name || 'Participant'} est déjà sur ${prestationNumbers} ce jour`,
              { title: 'Conflit détecté', duration: 5000 }
            );

            setConflicts(prev => {
              const filtered = prev.filter(c => c.user_id !== userId);
              const markedConflicts = thisUserConflicts.map(c => ({ ...c, isUnsaved: true }));
              return [...filtered, ...markedConflicts];
            });
          }
        }
      } catch (error) {
        console.error('Erreur vérification conflit:', error);
      }

      setDayParticipants(prev => ({
        ...prev,
        [currentDay.date]: [...(prev[currentDay.date] || []), userId]
      }));
    }
  }, [isParticipant, currentDay, setDayParticipants, setParticipantOnSite, token, sessionId, allUsers, showWarning]);

  // Handler: Toggle "Sur place"
  const toggleOnSite = useCallback((userId, isOnSite) => {
    if (!currentDay?.date) return;
    setParticipantOnSite(prev => {
      const updated = { ...prev };
      if (!updated[currentDay.date]) {
        updated[currentDay.date] = {};
      }
      updated[currentDay.date][userId] = isOnSite;
      return updated;
    });
  }, [currentDay, setParticipantOnSite]);

  // Handler: Ajouter plusieurs participants en batch (UNE seule requête de conflits)
  const addMultipleParticipants = useCallback(async (userIds) => {
    if (!userIds || userIds.length === 0) return { added: 0, conflicts: [] };

    // Filtrer ceux déjà participants
    const toAdd = userIds.filter(id => !isParticipant(id));
    if (toAdd.length === 0) return { added: 0, conflicts: [] };

    let conflictsFound = [];

    // UNE seule requête de conflits pour tous les participants
    try {
      const response = await fetch(API_ENDPOINTS.PRESTATION_CHECK_CONFLICTS, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          participant_ids: toAdd,
          start_date: currentDay.date,
          end_date: currentDay.date,
          exclude_session_id: sessionId
        })
      });

      if (response.ok) {
        const data = await response.json();
        conflictsFound = data.data || [];
      }
    } catch (error) {
      console.error('Erreur vérification conflits batch:', error);
    }

    // Ajouter les conflits détectés
    if (conflictsFound.length > 0) {
      setConflicts(prev => {
        const existingIds = new Set(prev.map(c => c.user_id));
        const newConflicts = conflictsFound
          .filter(c => !existingIds.has(c.user_id))
          .map(c => ({ ...c, isUnsaved: true }));
        return [...prev, ...newConflicts];
      });
    }

    // Ajouter tous les participants d'un coup
    setDayParticipants(prev => ({
      ...prev,
      [currentDay.date]: [...(prev[currentDay.date] || []), ...toAdd]
    }));

    return { added: toAdd.length, conflicts: conflictsFound };
  }, [isParticipant, currentDay, token, sessionId, setDayParticipants]);

  // Handler: Ajouter groupe complet (batch optimisé)
  const addTeamMembers = useCallback(async (groupId) => {
    const groupMembers = allUsers.filter(user => {
      if (!user.group_ids?.includes(groupId) && user.group_id !== groupId) return false;
      const status = userAvailability[user.id]?.status;
      return status === 'available' || !status;
    });
    const group = teams.find(t => t.id === groupId);

    const userIdsToAdd = groupMembers.map(u => u.id);
    const { added, conflicts: conflictsFound } = await addMultipleParticipants(userIdsToAdd);

    if (added > 0) {
      let message = `Groupe ${group?.name} : ${added} membres ajoutés`;
      if (conflictsFound.length > 0) {
        message += ` (${conflictsFound.length} en conflit)`;
        showWarning(message);
      } else {
        showSuccess(message);
      }
    } else {
      showInfo(`Tous les membres disponibles du groupe ${group?.name} sont déjà ajoutés`);
    }
  }, [allUsers, teams, userAvailability, addMultipleParticipants, showSuccess, showInfo, showWarning]);

  // Handler: Ajouter disponibles de la page (batch optimisé)
  const handleAddAvailable = useCallback(async () => {
    const availableUsers = paginatedUsers.users.filter(u => {
      const status = userAvailability[u.id]?.status;
      return status === 'available' || !status;
    });

    const userIdsToAdd = availableUsers.map(u => u.id);
    const { added, conflicts: conflictsFound } = await addMultipleParticipants(userIdsToAdd);

    if (added > 0) {
      let message = `${added} participants ajoutés`;
      if (conflictsFound.length > 0) {
        message += ` (${conflictsFound.length} en conflit)`;
        showWarning(message);
      } else {
        showSuccess(message);
      }
    }
  }, [paginatedUsers.users, userAvailability, addMultipleParticipants, showSuccess, showWarning]);

  // Handler: Retirer participants de la page
  const handleRemovePage = useCallback(() => {
    paginatedUsers.users.forEach(u => {
      if (isParticipant(u.id)) {
        toggleParticipant(u.id);
      }
    });
  }, [paginatedUsers.users, isParticipant, toggleParticipant]);

  // Handler: Reprendre jour précédent
  const addPreviousDayParticipants = useCallback(async () => {
    if (!isMultiDay || activeDayTab === 0) return;

    const previousDay = multiDayInfo.days[activeDayTab - 1];
    const previousParticipants = previousDay ? dayParticipants[previousDay.date] || [] : [];

    if (previousParticipants.length === 0) {
      showInfo('Aucun participant dans le jour précédent');
      return;
    }

    setLoadingConflicts(true);
    try {
      const conflictResponse = await fetch(API_ENDPOINTS.PRESTATION_CHECK_CONFLICTS, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          participant_ids: previousParticipants,
          start_date: currentDay.date,
          end_date: currentDay.date,
          session_id: sessionId
        })
      });

      if (conflictResponse.ok) {
        const conflictData = await conflictResponse.json();
        const conflictsFound = conflictData.data || conflictData.conflicts || [];

        const participantsToAdd = previousParticipants.filter(id =>
          !conflictsFound.some(c => c.user_id === id)
        );

        if (participantsToAdd.length > 0) {
          setDayParticipants(prev => ({
            ...prev,
            [currentDay.date]: [...(prev[currentDay.date] || []), ...participantsToAdd]
          }));

          // Copier aussi le statut "sur place"
          if (previousDay && participantOnSite[previousDay.date]) {
            setParticipantOnSite(prev => {
              const updated = { ...prev };
              if (!updated[currentDay.date]) {
                updated[currentDay.date] = {};
              }
              participantsToAdd.forEach(userId => {
                if (participantOnSite[previousDay.date]?.[userId]) {
                  updated[currentDay.date][userId] = participantOnSite[previousDay.date][userId];
                }
              });
              return updated;
            });
          }

          showSuccess(`${participantsToAdd.length} participants ajoutés du jour précédent`);
        }

        if (conflictsFound.length > 0) {
          setConflicts(conflictsFound);
          showWarning(`${conflictsFound.length} participants en conflit non ajoutés`);
        }
      }
    } catch (error) {
      console.error('Erreur vérification conflits:', error);
      showError('Erreur lors de la vérification des conflits');
    } finally {
      setLoadingConflicts(false);
    }
  }, [isMultiDay, activeDayTab, multiDayInfo.days, dayParticipants, currentDay, participantOnSite, setDayParticipants, setParticipantOnSite, token, sessionId, showSuccess, showInfo, showWarning, showError]);

  // Handler: Résolution conflit
  const handleConflictResolve = useCallback((replacementData) => {
    if (replacementData?.oldUserId && replacementData?.newUserId) {
      if (replacementData.isLocalReplace) {
        setDayParticipants(prev => ({
          ...prev,
          [currentDay.date]: [...(prev[currentDay.date] || []), replacementData.newUserId]
        }));
      } else {
        setDayParticipants(prev => {
          const updated = { ...prev };
          Object.keys(updated).forEach(date => {
            const idx = updated[date].indexOf(replacementData.oldUserId);
            if (idx !== -1) {
              updated[date] = [...updated[date]];
              updated[date][idx] = replacementData.newUserId;
            }
          });
          return updated;
        });
      }
      setConflicts(prev => prev.filter(c => c.user_id !== replacementData.oldUserId));
    } else {
      setShowConflictModal(true);
    }
  }, [currentDay, setDayParticipants]);

  // Handler: Retirer participant (depuis ConflictAlert)
  const handleRemoveParticipant = useCallback((userId) => {
    setDayParticipants(prev => ({
      ...prev,
      [currentDay.date]: prev[currentDay.date]?.filter(id => id !== userId) || []
    }));
    setConflicts(prev => prev.filter(c => c.user_id !== userId));
  }, [currentDay, setDayParticipants]);

  return (
    <div className="pi-container">
      {/* Navigation des jours */}
      <DayTabNavigation
        days={multiDayInfo.days}
        activeDayTab={activeDayTab}
        dayParticipants={dayParticipants}
        deletingDay={deletingDay}
        onDayChange={setActiveDayTab}
        onDeleteDay={handleDeleteDay}
      />

      {/* Sélecteur chef d'équipe par jour */}
      {currentDay && teamLeaders.length > 0 && formData?.team_leader_id && (
        <DayTeamLeaderSelector
          day={currentDay}
          prestationTeamLeaderId={formData.team_leader_id}
          currentOverride={dayTeamLeaders[currentDay.date]}
          teamLeaders={teamLeaders}
          dayParticipants={currentParticipants}
          token={token}
          sessionId={sessionId}
          onUpdate={(date, teamLeaderId) => {
            setDayTeamLeaders(prev => ({ ...prev, [date]: teamLeaderId }));
          }}
          onParticipantsChange={(date, participants) => {
            setDayParticipants(prev => ({ ...prev, [date]: participants }));
          }}
          showSuccess={showSuccess}
          showError={showError}
          showWarning={showWarning}
        />
      )}

      {/* Alerte conflits */}
      {conflicts.length > 0 && (
        <ConflictAlert
          conflicts={conflicts}
          onResolve={handleConflictResolve}
          onRemoveParticipant={handleRemoveParticipant}
          loading={loadingConflicts}
          token={token}
          sessionId={sessionId}
          startDate={currentDay?.date || startDate}
          endDate={currentDay?.date || endDate}
          currentParticipantIds={currentParticipants}
          showSuccess={showSuccess}
          showError={showError}
        />
      )}

      {/* Toggle vue/ajout */}
      <div className="pi-view-toggle">
        <button
          type="button"
          onClick={() => setViewMode('participants')}
          className={`pi-view-toggle__btn pi-view-toggle__btn--participants ${viewMode === 'participants' ? 'pi-view-toggle__btn--active' : ''}`}
        >
          Participants ({currentParticipants.length})
        </button>
        <button
          type="button"
          onClick={() => setViewMode('add')}
          className={`pi-view-toggle__btn pi-view-toggle__btn--add ${viewMode === 'add' ? 'pi-view-toggle__btn--active' : ''}`}
        >
          + Ajouter participants
        </button>
      </div>

      {/* Mode: Vue participants */}
      {viewMode === 'participants' && (
        <div>
          <div className="pi-stats-bar">
            <div>
              <span className="pi-stats-bar__count">
                {currentParticipants.length} participants {isMultiDay ? `pour ${currentDay?.label}` : 'sélectionnés'}
              </span>
              {conflicts.length > 0 && (
                <span className="pi-stats-bar__conflict">
                  • {conflicts.length} en conflit
                </span>
              )}
            </div>
            {isMultiDay && activeDayTab > 0 && (
              <button
                type="button"
                onClick={addPreviousDayParticipants}
                disabled={loadingConflicts}
                className="pi-quick-btn pi-quick-btn--success"
                style={{ fontSize: '12px' }}
              >
                Reprendre jour précédent ({dayParticipants[multiDayInfo.days[activeDayTab - 1]?.date]?.length || 0})
              </button>
            )}
          </div>

          {currentParticipants.length === 0 ? (
            <div className="pi-empty-state">
              <div className="pi-empty-state__icon">👥</div>
              <div className="pi-empty-state__title">
                Aucun participant {isMultiDay ? 'pour ce jour' : 'sélectionné'}
              </div>
              <div className="pi-empty-state__subtitle">
                Utilisez "Ajouter participants" pour commencer
              </div>
            </div>
          ) : (
            <div>
              {selectedParticipants.map(user => (
                <CompactParticipantItem
                  key={user.id}
                  user={user}
                  isSelected={true}
                  showCheckbox={false}
                  isOnSite={currentDay?.date && participantOnSite[currentDay.date]?.[user.id]}
                  userAvailability={userAvailability}
                  conflicts={conflicts}
                  onToggle={toggleParticipant}
                  onToggleOnSite={toggleOnSite}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Mode: Ajout participants */}
      {viewMode === 'add' && (
        <div>
          <SearchAndFilters
            search={search}
            teamFilter={teamFilter}
            teams={teams}
            users={allUsers}
            userAvailability={userAvailability}
            loading={loadingConflicts}
            canCreateInterim={canCreateInterim}
            paginatedUsers={paginatedUsers}
            onSearchChange={setSearch}
            onTeamFilterChange={setTeamFilter}
            onAddAvailable={handleAddAvailable}
            onRemovePage={handleRemovePage}
            onAddTeamMembers={addTeamMembers}
            onCreateInterim={() => setShowQuickInterimForm(true)}
          />

          <div className="pi-stats-bar">
            <div>
              <span className="pi-stats-bar__count">{currentParticipants.length}</span> participants {isMultiDay ? 'ce jour' : 'sélectionnés'}
              <span className="pi-stats-bar__conflict"> • {paginatedUsers.availableCount} disponibles</span>
            </div>
            <div>
              {paginatedUsers.total} résultats • Page {page + 1}
              {paginatedUsers.totalPages > 1 && ` / ${paginatedUsers.totalPages}`}
            </div>
          </div>

          <div style={{ maxHeight: '400px', overflow: 'auto' }}>
            {paginatedUsers.users.length === 0 ? (
              <div className="pi-empty-state">
                <div className="pi-empty-state__icon">🔍</div>
                <div className="pi-empty-state__title">Aucun participant trouvé</div>
                <div className="pi-empty-state__subtitle">
                  Essayez de modifier vos critères de recherche
                </div>
              </div>
            ) : (
              paginatedUsers.users.map(user => (
                <CompactParticipantItem
                  key={user.id}
                  user={user}
                  isSelected={isParticipant(user.id)}
                  showCheckbox={true}
                  userAvailability={userAvailability}
                  conflicts={conflicts}
                  onToggle={toggleParticipant}
                />
              ))
            )}
          </div>

          {paginatedUsers.totalPages > 1 && (
            <div className="pi-pagination">
              <button
                type="button"
                onClick={() => setPage(Math.max(0, page - 1))}
                disabled={!paginatedUsers.hasPrev}
                className="pi-pagination__btn"
              >
                ← Précédent
              </button>
              <span className="pi-pagination__info">
                Page {page + 1} / {paginatedUsers.totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage(page + 1)}
                disabled={!paginatedUsers.hasMore}
                className="pi-pagination__btn"
              >
                Suivant →
              </button>
            </div>
          )}
        </div>
      )}

      {/* Modale résolution conflits */}
      {showConflictModal && conflicts.length > 0 && (
        <ConflictResolutionModal
          conflicts={conflicts.map(c => ({
            id: c.user_id,
            type: 'participant',
            description: `${c.user_name || 'Participant'} est en conflit`,
            conflicted_participants: [{
              id: c.user_id,
              name: c.user_name,
              conflicting_sessions: c.conflicts?.map(conf => ({
                session_id: conf.session_id,
                prestation_number: conf.prestation_number
              })) || []
            }],
            start_date: currentDay?.date || startDate,
            end_date: currentDay?.date || endDate
          }))}
          onResolve={() => {
            setShowConflictModal(false);
            setConflicts([]);
            showSuccess('Conflits résolus avec succès');
          }}
          onCancel={() => setShowConflictModal(false)}
          token={token}
        />
      )}

      {/* Modale création intérimaire */}
      {showQuickInterimForm && (
        <QuickInterimForm
          onClose={() => setShowQuickInterimForm(false)}
          onSuccess={handleInterimCreated}
          onSelectExisting={handleSelectExistingInterim}
          pendingReview={true}
          prestationDayId={currentDay?.id}
        />
      )}
    </div>
  );
});

ParticipantsInterface.displayName = 'ParticipantsInterface';

export default ParticipantsInterface;
